/**
 * AuthStatusPage — autenticazione Phase 5 (IMPL-P5-007).
 *
 * Reusable status page for terminal flows (verify-email success/error,
 * signup-confirm "controlla la casella", reset-password done, etc.).
 *
 * Tones map to neutral CSS-variable-aware utility classes so dark/light mode
 * keeps working without hardcoded hex colors. The icon is rendered as a
 * Lucide component, sized 12 (h-12 w-12).
 */
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AuthLayout } from './AuthLayout';
import { AuthCard } from './AuthCard';

export type StatusTone = 'success' | 'error' | 'warning' | 'info';

interface ActionDescriptor {
  label: string;
  /** Internal route. Mutually exclusive with `onClick`. */
  to?: string;
  /** External href (e.g. `mailto:`, support page). */
  href?: string;
  /** Imperative handler (e.g. resend email, retry). */
  onClick?: () => void;
}

interface AuthStatusPageProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  tone: StatusTone;
  title: ReactNode;
  description?: ReactNode;
  primaryAction?: ActionDescriptor;
  secondaryAction?: ActionDescriptor;
  /** Optional extra content rendered between the description and the actions. */
  children?: ReactNode;
}

const TONE_RING: Record<StatusTone, string> = {
  success: 'bg-primary/10 text-primary',
  error: 'bg-destructive/10 text-destructive',
  warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  info: 'bg-primary/10 text-primary',
};

function renderAction(action: ActionDescriptor, variant: 'default' | 'outline') {
  if (action.to) {
    return (
      <Button asChild variant={variant} className="w-full">
        <Link to={action.to}>{action.label}</Link>
      </Button>
    );
  }
  if (action.href) {
    return (
      <Button asChild variant={variant} className="w-full">
        <a href={action.href}>{action.label}</a>
      </Button>
    );
  }
  return (
    <Button type="button" variant={variant} className="w-full" onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

export function AuthStatusPage({
  icon: Icon,
  tone,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
}: AuthStatusPageProps) {
  return (
    <AuthLayout>
      <AuthCard title={title} description={description}>
        <div className="flex flex-col items-center gap-6 text-center">
          <div className={cn('flex h-16 w-16 items-center justify-center rounded-full', TONE_RING[tone])}>
            <Icon className="h-8 w-8" aria-hidden="true" />
          </div>
          {children ? <div className="w-full">{children}</div> : null}
          {(primaryAction || secondaryAction) && (
            <div className="flex w-full flex-col gap-2">
              {primaryAction ? renderAction(primaryAction, 'default') : null}
              {secondaryAction ? renderAction(secondaryAction, 'outline') : null}
            </div>
          )}
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
